// Shared operator semantics for conditionalDisplay rules (QPBI-395).
// All four evaluators — backend completion engine, advisor wizard, public flow
// and the PDF builder — resolve `fieldValue` their own way but MUST delegate
// the operator comparison here so a rule always reaches the same verdict a
// person reading the control would.

import {
  canonicalizeDateTime,
  normalizeVisibleValue,
  parseMultiselectValue,
  parseNumberLenient,
  valueContains,
} from "./field-types";
import { ISO_DATE_PATTERN } from "./iso-date";

export type ConditionalOperator =
  | "equals"
  | "not_equals"
  | "greater_than"
  | "less_than"
  | "is_empty"
  | "is_not_empty"
  | "contains"
  | "not_contains"
  /**
   * QL-120 — set membership: `conditionValue` is a JSON array of ids and the
   * rule asks whether the (scalar) field value is one of them. The inverse of
   * `contains`, where the FIELD holds the set. Offered only for the goal-type
   * sentinel; for typed sources both return fixed verdicts (see below).
   */
  | "in"
  | "not_in";

/** Shape of a `conditionalDisplay` block parsed out of a page/layout/component config. */
export type ConditionalDisplayConfig = {
  enabled: boolean;
  sourceFieldId: string;
  operator: ConditionalOperator;
  value?: string;
};

/**
 * Operators that compare against a rule value. Saving one with an empty value
 * is refused: an empty "greater than" never matches and would silently hide
 * the element for every client.
 */
export const VALUE_REQUIRED_OPERATORS: readonly ConditionalOperator[] = [
  "equals",
  "not_equals",
  "greater_than",
  "less_than",
];

export function isValueRequiredOperator(operator: string): boolean {
  return (VALUE_REQUIRED_OPERATORS as readonly string[]).includes(operator);
}

/**
 * Compare a stored field value against a rule.
 *
 * `fieldType` is the source flow field's type ("boolean" | "multiselect" |
 * "number" | "dropdown" | …). Pass `undefined` for virtual/sentinel sources
 * (`__client_type__`, portfolio type/schedule, risk score, …) — those produce
 * canonical values and keep the historical lenient string semantics.
 *
 * Typed comparisons see only what the control renders (normalizeVisibleValue):
 * equals is case-sensitive, contains on multiselect is pure membership (no
 * scalar fallback), greater/less parse numbers leniently ("1,5" → 1.5) and
 * compare dates as yyyy-MM-dd days (QL-209). Datetime answers and rule values
 * are compared as instants, so the same moment written with different offsets
 * is equal (QL-211).
 */
export function compareFieldCondition(
  fieldValue: string,
  operator: ConditionalOperator,
  conditionValue: string,
  fieldType?: string,
): boolean {
  if (fieldType === undefined) {
    return compareLenient(fieldValue, operator, conditionValue);
  }

  const visible = normalizeVisibleValue(fieldType, fieldValue);

  switch (operator) {
    case "equals":
    case "not_equals": {
      let eq: boolean;
      if (fieldType === "number") {
        const n = parseNumberLenient(fieldValue);
        const c = parseNumberLenient(conditionValue);
        eq = n !== null && c !== null ? n === c : visible !== null && visible === conditionValue;
      } else if (fieldType === "datetime") {
        const a = canonicalizeDateTime(fieldValue);
        const c = canonicalizeDateTime(conditionValue);
        eq = a !== null && c !== null ? a === c : visible !== null && visible === conditionValue;
      } else {
        eq = visible !== null && visible === conditionValue;
      }
      return operator === "equals" ? eq : !eq;
    }
    case "greater_than":
    case "less_than": {
      if (fieldType === "date") {
        // QL-209 — date answers and rule values are both yyyy-MM-dd, so the
        // string order is the calendar order. Anything else never matches.
        if (!ISO_DATE_PATTERN.test(fieldValue) || !ISO_DATE_PATTERN.test(conditionValue))
          return false;
        return operator === "greater_than"
          ? fieldValue > conditionValue
          : fieldValue < conditionValue;
      }
      if (fieldType === "datetime") {
        // QL-211 — the canonical UTC form has a fixed width, so its string
        // order is the chronological order. Anything unparseable never matches.
        const a = canonicalizeDateTime(fieldValue);
        const c = canonicalizeDateTime(conditionValue);
        if (a === null || c === null) return false;
        return operator === "greater_than" ? a > c : a < c;
      }
      const n = parseNumberLenient(fieldValue);
      const c = parseNumberLenient(conditionValue);
      if (n === null || c === null) return false;
      return operator === "greater_than" ? n > c : n < c;
    }
    case "is_empty":
      return visible === null;
    case "is_not_empty":
      return visible !== null;
    case "contains":
    case "not_contains": {
      const has =
        fieldType === "multiselect"
          ? parseMultiselectValue(fieldValue).includes(conditionValue)
          : visible !== null && visible === conditionValue;
      return operator === "contains" ? has : !has;
    }
    // QL-120 — deliberately fixed verdicts, not a fall-through to the
    // permissive default: the set operators are not offered for typed sources
    // in the authoring UI, and a defined hide is safer than an accidental show.
    // Exposing them for typed sources later is a behavioural change.
    case "in":
      return false;
    case "not_in":
      return true;
    default:
      return true; // Unknown operator = show by default
  }
}

/** Historical string semantics, kept verbatim for sentinel sources. */
function compareLenient(
  fieldValue: string,
  operator: ConditionalOperator,
  conditionValue: string,
): boolean {
  switch (operator) {
    case "equals":
      return fieldValue.toLowerCase() === conditionValue.toLowerCase();
    case "not_equals":
      return fieldValue.toLowerCase() !== conditionValue.toLowerCase();
    case "greater_than": {
      const n = Number.parseFloat(fieldValue);
      const c = Number.parseFloat(conditionValue || "0");
      return !Number.isNaN(n) && !Number.isNaN(c) && n > c;
    }
    case "less_than": {
      const n = Number.parseFloat(fieldValue);
      const c = Number.parseFloat(conditionValue || "0");
      return !Number.isNaN(n) && !Number.isNaN(c) && n < c;
    }
    case "is_empty":
      return !fieldValue || fieldValue.trim() === "";
    case "is_not_empty":
      return !!(fieldValue && fieldValue.trim() !== "");
    case "contains":
      return valueContains(fieldValue, conditionValue);
    case "not_contains":
      return !valueContains(fieldValue, conditionValue);
    // QL-120 — set membership. Malformed/empty JSON parses to [] so `in` hides
    // and `not_in` shows with no special case. Case-insensitive like `equals`.
    case "in":
    case "not_in": {
      const needle = fieldValue.toLowerCase();
      const has = parseMultiselectValue(conditionValue).some((v) => v.toLowerCase() === needle);
      return operator === "in" ? has : !has;
    }
    default:
      return true; // Unknown operator = show by default
  }
}
